import {
  Alert,
  AlertDescription,
  Button,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@racefront/shadcn-ui'
import { AlertCircle, Info, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { FormattedMessage } from 'react-intl'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { APP_ROUTES } from '../../../constants/routes'
import { ApiError } from '../../../types/api-error.type'
import { useAuth } from '../../data/hooks/auth'
import { useAuthStore } from '../../data/stores/auth.store'

const COUNTDOWN = 45 // seconds
const CODE_VALIDITY = 10 // minutes

export default function Verify() {
  const { initSession } = useAuthStore()
  const {
    verifyAuthSession,
    verifAuthError,
    verifyAuthLoading,
    initiateAuthSession,
    initiateAuthData,
    initiateAuthLoading,
  } = useAuth()
  const nav = useNavigate()
  const [params] = useSearchParams()

  const [code, setCode] = useState('')
  const [countdown, setCountdown] = useState(COUNTDOWN)
  const [canResend, setCanResend] = useState(false)

  // Start countdown timer when component mounts (initial code sent)
  useEffect(() => {
    setCountdown(COUNTDOWN)
    setCanResend(false)
  }, [])

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [countdown])

  const handleResend = () => {
    if (initSession && canResend && !initiateAuthLoading) {
      setCode('')
      setCountdown(COUNTDOWN)
      setCanResend(false)
      initiateAuthSession(initSession.username, initSession.otpMedium)
    }
  }

  const handleResendAlternative = () => {
    if (initSession && canResend && !initiateAuthLoading) {
      setCode('')
      setCountdown(COUNTDOWN)
      setCanResend(false)
      // Determine which mode to use based on current OTP medium
      const modeParam = initSession.otpMedium === 'EMAIL' ? 'phone' : 'email'
      // Preserve existing query params like redirectUri
      const searchParams = new URLSearchParams(params)
      searchParams.set('mode', modeParam)
      nav(`${APP_ROUTES.SignIn}?${searchParams.toString()}`, { replace: true })
    }
  }

  const handleStartOver = () => {
    nav(APP_ROUTES.SignIn)
  }

  const handleVerify = () => {
    if (initSession) {
      verifyAuthSession(initSession.username, initSession.otpMedium, code, initSession.id)
    }
  }

  useEffect(() => {
    if (verifAuthError) {
      const err = verifAuthError as ApiError
      if (err.error === 'code_mismatch') {
        toast.error(<FormattedMessage id="verify.error.codeMismatch" defaultMessage="The code entered is incorrect." />)
      }
      if (err.error === 'invalid_session') {
        toast(
          (t) => (
            <div className="flex flex-row gap-2">
              <AlertCircle className="text-destructive" />
              <p>
                <FormattedMessage id="verify.error.invalidSession" defaultMessage="Session is invalid or expired" />
              </p>
              <Button
                variant={'link'}
                onClick={() => {
                  toast.dismiss(t.id)
                  nav(APP_ROUTES.SignIn)
                }}
              >
                <FormattedMessage id="verify.startOver" defaultMessage="Start over" />
              </Button>
            </div>
          ),
          { duration: 1000 * 10 },
        )
      }
    }
  }, [nav, verifAuthError])

  useEffect(() => {
    if (initiateAuthData) {
      toast.success(<FormattedMessage id="verify.otpSent" defaultMessage="Code just sent" />)
      // Reset countdown when new code is sent
      setCountdown(COUNTDOWN)
      setCanResend(false)
    }
  }, [initiateAuthData])

  if (!initSession) {
    return (
      <div className="flex w-full flex-col items-center">
        <div className="flex w-full max-w-sm flex-col items-center">
          <h3 className="text-center text-xl font-bold">
            <FormattedMessage id="verify.noSession.title" defaultMessage="No active session" />
          </h3>
          <div className="mt-4 text-center">
            <p>
              <FormattedMessage
                id="verify.noSession.message"
                defaultMessage="Please start the sign-in process again."
              />
            </p>
          </div>
          <Button onClick={() => nav(APP_ROUTES.SignIn)} className="mt-6" variant="link">
            <FormattedMessage id="verify.noSession.signIn" defaultMessage="Go to Sign In" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-center">
      <div className="flex w-full max-w-sm flex-col items-center">
        <h3 className="text-center text-xl font-bold">
          <FormattedMessage id="verify.title" defaultMessage="Verify Code" />
        </h3>
        <div className="mt-4 text-center">
          <p>
            <FormattedMessage id="verify.message" defaultMessage="We've just sent you a code to" />
          </p>
          <p>{initSession?.preview}</p>
        </div>

        <div className="mt-6">
          <InputOTP autoFocus maxLength={8} onChange={setCode}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
              <InputOTPSlot index={6} />
              <InputOTPSlot index={7} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <Alert className="mt-6">
          <Info className="h-4 w-4" />
          <AlertDescription className="space-y-1">
            <p>
              <FormattedMessage
                id="verify.info.otpValidityMsg"
                defaultMessage="Valid for 5 minutes"
                values={{ minutes: CODE_VALIDITY }}
              />
            </p>
            {initSession?.otpMedium === 'EMAIL' && (
              <p>
                <FormattedMessage
                  id="verify.info.email.checkSpam"
                  defaultMessage="If you don't see the email, please check your spam folder."
                />
              </p>
            )}
            {initSession?.otpMedium !== 'EMAIL' && (
              <p>
                <FormattedMessage
                  id="verify.info.phone.checkSignal"
                  defaultMessage="Make sure you have signal. Having trouble? Try email instead."
                />
              </p>
            )}
          </AlertDescription>
        </Alert>

        <Button onClick={handleResend} className="mt-6" variant="link" disabled={!canResend || initiateAuthLoading}>
          {canResend ? (
            <FormattedMessage id="verify.resendOtp" defaultMessage="Resend code" />
          ) : (
            <FormattedMessage
              id="verify.resendOtpCountdown"
              defaultMessage="Resend code ({countdown}s)"
              values={{ countdown }}
            />
          )}
        </Button>
        <Button onClick={handleResendAlternative} variant="link" disabled={!canResend || initiateAuthLoading}>
          {canResend ? (
            initSession?.otpMedium === 'EMAIL' ? (
              <FormattedMessage id="verify.resendToPhone" defaultMessage="Resend to phone instead" />
            ) : (
              <FormattedMessage id="verify.resendToEmail" defaultMessage="Resend to email instead" />
            )
          ) : initSession?.otpMedium === 'EMAIL' ? (
            <FormattedMessage
              id="verify.resendToPhoneCountdown"
              defaultMessage="Resend to phone instead ({countdown}s)"
              values={{ countdown }}
            />
          ) : (
            <FormattedMessage
              id="verify.resendToEmailCountdown"
              defaultMessage="Resend to email instead ({countdown}s)"
              values={{ countdown }}
            />
          )}
        </Button>
        <Button onClick={handleStartOver} variant="link">
          <FormattedMessage id="verify.wrongContact" defaultMessage="Wrong email or phone, start over" />
        </Button>
        <Button
          onClick={handleVerify}
          disabled={code?.length !== 8 || verifyAuthLoading || initiateAuthLoading}
          className="mt-2 w-full"
        >
          {verifyAuthLoading || (initiateAuthLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />)}
          <FormattedMessage id="verify.button" defaultMessage="Verify" />
        </Button>
      </div>
    </div>
  )
}
